import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { PrimeNgModule } from '../primeng/primeng.module';
import { BookingSchedule } from 'src/app/simulator-booking/models/booking-schedule';
import { Simulator } from 'src/app/simulator-booking/models/simulators';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BookingWidgetService } from './booking-widget.service';
import { DatePipe } from '@angular/common';
import { StripeElementsDirective, StripePaymentElementComponent, StripeService, injectStripe } from 'ngx-stripe';
import { StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AlertDialogPrimeNg } from '../primeng/dialogs/alert.dialog';
import { BookingStatusEnum } from 'src/app/shared/models/booking-status.enum';
import { CreditCardFeeType } from 'src/app/shared/models/creditcard-fee-type.enum';
import { Addon } from 'src/app/simulator-booking/models/addon';
import { PromoCodeDiscountType } from 'src/app/shared/models/promo-code-discount-type.enum';
import { PromoCode } from 'src/app/simulator-booking/models/promo-code';
import { PromoCodeService } from 'src/app/simulator-booking/services/promo-code.service';
import { OperatingHours } from 'src/app/simulator-booking/models/operating-hours';
import { DigitalWaiverWidgetService } from '../digital-waiver-widget/digital-waiver-widget.service';
import { DigitalWaiver } from 'src/app/simulator-booking/models/digital-waiver';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { BookingDurationService } from 'src/app/simulator-booking/services/booking-duration.service';
import { BookingDuration } from 'src/app/simulator-booking/models/booking-duration';

@Component({
  selector: 'booking-widget',
  imports: [
    PrimeNgModule,
    DatePipe,
    StripeElementsDirective,
    StripePaymentElementComponent
  ],
  templateUrl: './booking-widget.component.html',
  styleUrl: './booking-widget.component.scss',
})

export class BookingWidgetComponent implements OnInit {
  readonly stripe = injectStripe();

  @ViewChild(StripeElementsDirective) elements!: StripeElementsDirective;
  @ViewChild(StripePaymentElementComponent) paymentElement!: StripePaymentElementComponent;

  public elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'stripe'
    },
    locale: 'en',
  };

  public paymentElementsOptions: StripePaymentElementOptions = {
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    }
  };

  public isCardValid: boolean = false;

  dialogRef: DynamicDialogRef;

  public stripePaymentIntentData: any;
  private paymentIntent: any;

  public simulators: Simulator[] = [];
  public availableTimeSlots: any[] = [];
  public availableAddons: Addon[] = [];
  public bookingDurations: BookingDuration[] = [];
  public booking: BookingSchedule = new BookingSchedule();
  private apiKey: string;
  private destroyed$ = new Subject<void>();
  public messages: any;
  public userDetails: any;
  private activeDigitalWaiver: DigitalWaiver;
  public minDate: Date | undefined;
  public maxDate: Date | undefined;

  public selectedDate: Date | undefined;
  public selectedSimulator: Simulator;
  public selectedDuration: BookingDuration;
  public selectedTimeSlot: any;
  public customerName: string;
  public email: string;
  public phoneNumber: string;
  public description: string;
  public participants: number;
  public selectedAddons: Addon[] = [];
  private customerResponse: any;
  public loadingStates = {
    page: false,
    timeSlots: false,
    payment: false,
    submission: false,
    promoCode: false
  };

  public totalPrice: number = 0;

  showPromoCodeInput = false;
  promoCode: string = '';
  appliedPromoCode: PromoCode | null = null;
  DiscountType = PromoCodeDiscountType;

  route = inject(ActivatedRoute);

  constructor(private bookingWidgetService: BookingWidgetService,
    protected stringHelpers: StringHelpers,
    private ngxStripeService: StripeService,
    private dialogService: DialogService,
    private promoCodeService: PromoCodeService,
    private digitalWaiverWidgetService: DigitalWaiverWidgetService) {
  }

  ngOnInit(): void {
    this.loadingStates.page = true;
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe((params: any) => {
      this.apiKey = params.get('apiKey');
      this.getUserOrganization();

      // Set min date to today
      this.minDate = new Date();

      // Set max date to last day of next month
      const today = new Date();
      this.maxDate = new Date(today.getFullYear(), today.getMonth() + 3, 0);

      this.destroyed$.unsubscribe();
    });
  }

  getActiveDigitalWaiver() {
    this.digitalWaiverWidgetService.getActiveDigitalWaiver(this.userDetails.organization.id, this.apiKey).subscribe({
      next: (result: any) => {
        this.activeDigitalWaiver = result;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  getUserOrganization() {
    this.bookingWidgetService.getUserOrganization(this.apiKey).subscribe({
      next: (result) => {
        this.userDetails = result;
        this.getAddons();
        this.getActiveDigitalWaiver();
        this.getBookingDurations();
      },
      error: (error) => {
        console.log("user account error");
        console.log(error);
        this.setMessage("Unabled to get User Organization Details", 'error');
        this.loadingStates.page = false;
      },
      complete: () => this.getSimulators()
    });
  }

  getBookingDurations() {
    this.bookingWidgetService.getBookingDurations(this.apiKey, { organizationId: this.userDetails.organization.id }).subscribe({
      next: (result) => {
        this.bookingDurations = result;
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => this.loadingStates.page = false
    });
  }

  getSimulators() {
    let data = { organizationId: this.userDetails.organization.id };
    this.bookingWidgetService.getSimulators(this.apiKey, data).subscribe({
      next: (result) => {
        this.simulators = result.results.filter(x => x.isAvailable === true);
      },
      error: (error) => {
        console.error(error);
        this.setMessage(error.error.Error, 'error');
        this.loadingStates.page = false;
      },
      complete: () => this.loadingStates.page = false
    })
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? ' hour' : ' hours'}`;
    }
    return `${hours} hours ${remainingMinutes} minutes`;
  }

  getAvailableTimeSlots() {
    if (this.selectedDuration !== undefined) {
      this.loadingStates.timeSlots = true;
      let data = {
        simulatorId: this.selectedSimulator.id,
        durationMinutes: this.selectedDuration.durationMinutes,
        startDate: this.stringHelpers.normalizeDateTime(this.selectedDate),
        organizationId: this.userDetails.organization.id,
        operatingHours: this.userDetails.organization.operatingHours ?
          JSON.parse(this.userDetails.organization.operatingHours as unknown as string) as OperatingHours[] :
          [],
        timeZone: this.userDetails.organization.timeZone
      };

      this.bookingWidgetService.getAvailableTimeSlots(this.apiKey, data).subscribe({
        next: (result) => {
          this.availableTimeSlots = result;
        },
        error: (error) => {
          this.loadingStates.timeSlots = false;
          console.error(error);
          this.setMessage(error.error.Error, 'error');
        },
        complete: () => this.loadingStates.timeSlots = false
      });
    }
  }

  getAddons() {
    let data = { organizationId: this.userDetails.organization.id };
    this.bookingWidgetService.getAddons(this.apiKey, data).subscribe({
      next: (result) => {
        this.availableAddons = result.results;
      },
      error: (error) => {
        console.error(error);
        this.setMessage(error.error.Error, 'error');
      },
      complete: () => this.loadingStates.page = false
    });
  }

  async handleNext(activateCallback: any, step: number): Promise<number> {
    try {
      this.loadingStates.payment = true;

      // Calculate total price before creating payment intent
      this.calculateTotalPrice();

      await this.createCustomer();
      const result = await this.createPaymentIntent() as { client_secret: string };
      // Initialize Stripe Elements right before showing the payment form
      this.ngxStripeService.changeKey(environment.stripe.token, {
        stripeAccount: this.userDetails.organization.stripeAccountId
      });

      this.elementsOptions = {
        appearance: { theme: 'stripe' },
        locale: 'en',
        clientSecret: result.client_secret.toString(),
      } as StripeElementsOptions;

      setTimeout(() => {
        this.loadingStates.payment = false;
        return activateCallback(step);
      }, 100);
    } catch (error) {
      this.loadingStates.payment = false;
      console.error('Error in payment setup:', error);
      this.openAlertDialog('Error', 'Unable to setup payment. Please try again.');
      return activateCallback(step);
    }
  }

  async handleCancelPaymentBack(activateCallback: any, prevCallbackNumber: number): Promise<number> {
    await this.cancelPaymentIntent();
    this.messages = null;
    return activateCallback(prevCallbackNumber);
  }

  private cancelPaymentIntent(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bookingWidgetService.cancelPaymentIntent(this.apiKey, {
        connectedAccountId: this.userDetails.organization.stripeAccountId,
        paymentIntentId: this.paymentIntent.id
      }).subscribe({
        next: (result) => {
          this.elementsOptions = {
            appearance: { theme: 'stripe' },
            locale: 'en',
          } as StripeElementsOptions;
          resolve();
        },
        error: (error) => {
          console.error(error);
          this.setMessage(error, 'error');
          resolve(); // Resolve even on error to continue the flow
        }
      });
    });
  }

  private calculateTotalPrice(): void {
    // Calculate base price (timeslot + addons)
    let basePrice = this.selectedTimeSlot?.price || 0;
    if (this.selectedAddons?.length > 0) {
      basePrice += this.selectedAddons.reduce((sum, addon) => sum + addon.price, 0);
    }

    // Add credit card fee
    this.totalPrice = this.userDetails?.organization?.creditCardFeeType === CreditCardFeeType.Percentage ?
      basePrice * (1 + this.userDetails.organization.creditCardFee / 100) :
      basePrice + this.userDetails.organization.creditCardFee;

    if (this.appliedPromoCode) {
      const discount = this.appliedPromoCode.discountType === PromoCodeDiscountType.Percentage
        ? (this.totalPrice * this.appliedPromoCode.discountAmount / 100)
        : this.appliedPromoCode.discountAmount;
      this.totalPrice -= discount;
    }
  }

  async createPaymentIntent() {
    this.loadingStates.payment = true;

    let data = {
      accountId: this.userDetails.organization.stripeAccountId,
      options: {
        amount: Math.round(this.totalPrice * 100), // Convert to cents
        currency: 'usd',
        customer: this.customerResponse.customer.id,
        setup_future_usage: 'off_session',
        metadata: {
          Source: 'Booking Widget',
          CustomerName: this.customerName,
          CustomerPhone: this.phoneNumber,
          UserId: this.userDetails.id,
          Parent: 'true'
        }
      }
    }

    return new Promise((resolve, reject) => {
      this.bookingWidgetService.createPaymentIntent(this.apiKey, data).subscribe({
        next: (result: any) => {
          this.paymentIntent = result;
          this.loadingStates.payment = false;
          resolve(result);
        },
        error: (err) => {
          this.loadingStates.payment = false;
          console.error(err);
          reject(err);
        }
      });
    });
  }

  async createCustomer() {
    this.loadingStates.payment = true;

    return new Promise((resolve, reject) => {

      this.bookingWidgetService.createCustomer(this.apiKey, {
        accountId: this.userDetails.organization.stripeAccountId,
        options: {
          email: this.email,
          name: this.customerName
        }
      }).subscribe({
        next: (result) => {
          this.customerResponse = result;
          resolve(result);
        },
        error: (err) => {
          this.loadingStates.payment = false;
          console.error(err);
          reject(err);
        },
        complete: () => this.loadingStates.payment = false
      });
    });

  }

  submit() {
    if (!this.paymentElement?.elements) {
      this.openAlertDialog('Payment Error', 'Payment form not initialized');
      return;
    }

    this.loadingStates.submission = true;
    this.stripe.confirmPayment({
      elements: this.paymentElement.elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required'
    }).subscribe({
      next: (result) => {
        if (result.error) {
          this.openAlertDialog('Payment Error', `There was an error processing your payment. Please try again or contact the establishment for assistance.`);
          this.loadingStates.submission = false;
        } else {
          if (result.paymentIntent.status === 'succeeded') {
            this.stripePaymentIntentData = { hasPaid: true };  // Set the payment status
            this.createBooking();
          } else {
            this.openAlertDialog('Payment Unsuccessful', result.paymentIntent.status.toString());
            this.loadingStates.submission = false;
          }
        }
      },
      error: (err) => {
        this.loadingStates.submission = false;
        this.openAlertDialog('Payment Error', `There was an error processing your payment. Please try again. \n ${err}`);
        console.log(err);
      }
    });
  }

  private createBooking() {
    this.loadingStates.submission = true;
    let data = {
      startTime: this.stringHelpers.normalizeDateTime(this.selectedTimeSlot.startTime),
      endTime: this.stringHelpers.normalizeDateTime(this.selectedTimeSlot.endTime),
      status: BookingStatusEnum.Booked,
      simulatorId: this.selectedSimulator.id,
      participants: this.participants,
      totalPrice: this.totalPrice,
      hasPaid: true,
      phoneNumber: this.phoneNumber,
      email: this.email,
      customerName: this.customerName,
      organizationId: this.userDetails.organization.id,
      createdBy: this.userDetails.id,
      organization: this.userDetails.organization.organizationName,
      bay: this.selectedSimulator.bayName,
      addons: this.selectedAddons,
      promoCodeId: this.appliedPromoCode?.id,
      notes: this.description,
      stripePaymentIntentId: this.paymentIntent.id,
      timeZone: this.userDetails.organization.timeZone
    };

    this.bookingWidgetService.createBooking(this.apiKey, data).subscribe({
      next: (result) => {
        if (result.status === 200 || result.status === 202) {
          if (this.activeDigitalWaiver !== undefined && this.activeDigitalWaiver !== null) {
            let message = '';
            if (result.status === 202) {
              message = 'Your booking has been confirmed but there was a problem sending your digital waiver and confirmation email. Please check with establishment for your confirmation number and digital waiver.';
            } else {
              message = 'Your booking has been confirmed. Your confirmation number and digital waiver will be sent to your email shortly.';
            }
            this.createDigitalWaiverEntry(result.booking.id);
            this.openAlertDialog('Payment Successful', message);
          } else {
            this.openAlertDialog('Payment Successful', 'Your booking has been confirmed and your confirmation number will be sent to your email shortly.');
          }
          this.bookingWidgetService.updatePaymentIntent(this.apiKey, {
            paymentIntentId: this.paymentIntent.id,
            accountId: this.userDetails.organization.stripeAccountId,
            options: {
              metadata: {
                BookingId: result.booking.id
              }
            }
          }).subscribe();
          this.dialogRef.onClose.subscribe(() => {
            window.location.reload();
          });
        }
        this.loadingStates.submission = false;
      },
      error: (error) => {
        this.loadingStates.submission = false;
        console.error(error);
        this.setMessage('There was an error creating your booking. Please try again. If the problem persists, please contact the establishment for assistance.', 'error');
      },
      complete: () => {
        this.updatePromoCodeUsage();
      }
    });
  }

  private updatePromoCodeUsage() {
    if (this.appliedPromoCode) {
      this.appliedPromoCode.currentUsageCount++;
      this.promoCodeService.update(this.appliedPromoCode.id, this.appliedPromoCode).subscribe();
    }
  }

  private createDigitalWaiverEntry(bookingId: string) {
    let now = new Date();
    const nowUtc = new Date(Date.UTC(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      now.getMinutes()
    ));

    let data = {
      digitalWaiverId: this.activeDigitalWaiver.id,
      bookingId: bookingId,
      organizationId: this.userDetails.organization.id,
      firstName: this.customerName.split(' ')[0],
      lastName: this.customerName.split(' ').slice(1).join(' '),
      emailAddress: this.email,
      createdBy: this.userDetails.id,
      sentDate: nowUtc
    }

    this.digitalWaiverWidgetService.createDigitalWaiverEntry(this.apiKey, data).subscribe();
  }

  private openAlertDialog(title, body) {
    this.dialogRef = this.dialogService.open(AlertDialogPrimeNg, {
      header: title,
      width: '50vw',
      modal: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      data: {
        body: body,
        buttonText: 'Close'
      }
    });

    this.dialogRef.onClose.subscribe(() => {
      if (title.toLowerCase() === 'Success'.toLowerCase()) {
        // Reload the iframe after dialog closes
        window.location.reload();
      }
    });
  }

  isValid(properties: any[]): boolean {
    for (const property of properties) {
      if (property === null || property === undefined || property == '') {
        return false;
      }
    }

    return true;
  }

  isEmailValid(): boolean {
    if (!this.email) {
      return false;
    }
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(this.email);
  }

  onCardChange(event: any): void {
    this.isCardValid = event.complete && !event.error;
  }

  applyPromoCode() {
    if (!this.promoCode) return;

    this.loadingStates.promoCode = true;
    this.promoCodeService.getByCode({ code: this.promoCode }).subscribe({
      next: (promoCode) => {
        if (promoCode) {
          const now = new Date();
          const startDate = new Date(promoCode.startDate);
          const endDate = new Date(promoCode.endDate);

          if (!promoCode.isActive) {
            this.setMessage('This promo code is not active', 'error');
            return;
          }

          if (now < startDate || now > endDate) {
            this.setMessage('This promo code has expired or is not yet valid', 'error');
            return;
          }

          if (promoCode.usageLimit && promoCode.currentUsageCount >= promoCode.usageLimit) {
            this.setMessage('This promo code has reached its usage limit', 'error');
            return;
          }

          this.appliedPromoCode = promoCode;
          this.calculateTotalPrice();
        } else {
          this.setMessage('Invalid promo code', 'error');
        }
      },
      error: (error) => {
        this.setMessage('Invalid promo code', 'error');
        console.error(error);
        this.loadingStates.promoCode = false;
      },
      complete: () => {
        this.loadingStates.promoCode = false;
      }
    });
  }

  clearPromoCode() {
    this.showPromoCodeInput = false;
    this.promoCode = '';
    this.appliedPromoCode = null;
    this.messages = null;
    this.calculateTotalPrice(); // Recalculate total without discount
  }

  private setMessage(message: string, severity: string = 'error') {
    this.messages = { severity: severity, summary: message };
  }
}
